import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Pagination,
  Spinner,
  Form,
  InputGroup,
  Alert,
  CardFooter
} from 'react-bootstrap';
import {
  FaSearch,
  FaUser,
  FaSyncAlt,
  FaEdit,
  FaTrash,
  FaPlus,
  FaCheck,
  FaTimes
} from 'react-icons/fa';
import offlineDBService, { db } from 'db/dexieDB'; // Adjust path as needed
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { MdEdit } from 'react-icons/md';
import FalconCardHeader from 'components/common/FalconCardHeader';

function DynamicOfflineReport({ dataType = 'farmer' }) {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState({});
  const [deletingId, setDeletingId] = useState(null);

  const ITEMS_PER_PAGE = 20;

  // Determine what data to fetch based on dataType
  const loadData = async (page = 1, search = '') => {
    try {
      setLoading(true);
      setError(null);

      // Calculate offset based on page number
      const offset = (page - 1) * ITEMS_PER_PAGE;

      let itemsList = [];
      let totalCount = 0;

      if (dataType === 'farmer') {
        if (search) {
          // Search farmers
          const allSearchResults = await offlineDBService.searchFarmers(search);
          // Sort by creation date (newest first)
          allSearchResults.sort((a, b) => {
            if (a.createdAt && b.createdAt) {
              return new Date(b.createdAt) - new Date(a.createdAt);
            } else if (a.createdAt) return -1;
            else if (b.createdAt) return 1;
            else if (a.updatedAt && b.updatedAt) {
              return new Date(b.updatedAt) - new Date(a.updatedAt);
            }
            return 0;
          });
          itemsList = allSearchResults.slice(offset, offset + ITEMS_PER_PAGE);
          totalCount = allSearchResults.length;
        } else {
          // Get all farmers
          const allItems = await offlineDBService.getAllFarmers();
          // Sort by creation date (newest first)
          allItems.sort((a, b) => {
            if (a.createdAt && b.createdAt) {
              return new Date(b.createdAt) - new Date(a.createdAt);
            } else if (a.createdAt) return -1;
            else if (b.createdAt) return 1;
            else if (a.updatedAt && b.updatedAt) {
              return new Date(b.updatedAt) - new Date(a.updatedAt);
            }
            return 0;
          });
          itemsList = allItems.slice(offset, offset + ITEMS_PER_PAGE);
          totalCount = allItems.length;
        }
      } else if (dataType === 'farmland') {
        // Get farms data instead
        const allItems = (await offlineDBService.getAllFarms?.()) || [];
        allItems.sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            return new Date(b.createdAt) - new Date(a.createdAt);
          }
          return 0;
        });
        itemsList = allItems.slice(offset, offset + ITEMS_PER_PAGE);
        totalCount = allItems.length;
      } else if (dataType === 'cultivation') {
        // Get all cultivations
        const allItems = (await offlineDBService.getAllCultivations?.()) || [];

        // Sort cultivations by creation date (newest first)
        allItems.sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            return new Date(b.createdAt) - new Date(a.createdAt);
          }
          return 0;
        });

        // Paginate the results
        const paginatedItems = allItems.slice(offset, offset + ITEMS_PER_PAGE);
        totalCount = allItems.length;

        // Now, enhance each cultivation with resolved dropdown values
        const enhancedItems = await Promise.all(
          paginatedItems.map(async cultivation => {
            try {
              // Get farm name
              const farmName = await resolveDropdownValue(
                'farm',
                cultivation.farm
              );
              console.log(farmName, 'farmname');
              // Get crop name
              const cropName = await resolveDropdownValue(
                'crop',
                cultivation.crop
              );

              // Get season name
              const seasonName = await resolveDropdownValue(
                'season',
                cultivation.season
              );

              // Get area unit name
              const areaUnitName = await resolveDropdownValue(
                'area_uom',
                cultivation.cultivated_area_uom
              );

              // Return the enhanced cultivation object with names
              return {
                ...cultivation,
                farmName,
                cropName,
                seasonName,
                areaUnitName
              };
            } catch (error) {
              console.error(
                `Error resolving dropdown values for cultivation ${cultivation.id}:`,
                error
              );
              return {
                ...cultivation,
                farmName: 'Unknown',
                cropName: 'Unknown',
                seasonName: 'Unknown',
                areaUnitName: 'acres'
              };
            }
          })
        );

        itemsList = enhancedItems;
      }

      setData(itemsList);
      setTotalItems(totalCount);
      setCurrentPage(page);
    } catch (err) {
      console.error(`Error loading ${dataType} data:`, err);
      setError(`Failed to load data: ${err.desc}`);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  // Helper function to resolve dropdown value names
  const resolveDropdownValue = async (key, valueId) => {
    if (!valueId) return 'Not specified';

    const dropdownData = await offlineDBService.getDropdownData();
    try {
      console.log(
        `Resolving dropdown value for key: ${key}, valueId: ${valueId}`
      );

      // Get the dropdown options for this key
      // This was the bug - you were always using 'farm' instead of using the key parameter
      const dropdownRecord = dropdownData.results[key];

      console.log(
        `Retrieved dropdown record for ${key}:`,
        dropdownRecord,
        valueId
      );

      // if (!dropdownRecord || !dropdownRecord.results) {
      //   console.warn(`No dropdown data found for key: ${key}`);
      //   return 'Unknown';
      // }

      // Find the matching item in the dropdown data
      const item = dropdownRecord.find(item => item.value == valueId);

      console.log(`Found item for ${valueId}:`, item);

      // Return the name if found
      return item ? item.label : `Unknown (${valueId})`;
    } catch (error) {
      console.error(
        `Error resolving dropdown value for ${key}/${valueId}:`,
        error
      );
      return `Error (${valueId})`;
    }
  };

  // Load data on initial component mount or when dataType changes
  useEffect(() => {
    loadData(1, searchTerm);
  }, [dataType]);

  // Handle pagination change
  const handlePageChange = pageNumber => {
    loadData(pageNumber, searchTerm);
  };

  // Handle search
  const handleSearch = e => {
    e.preventDefault();
    setIsSearching(true);
    loadData(1, searchTerm);
  };

  // Reset search
  const resetSearch = () => {
    setSearchTerm('');
    loadData(1, '');
  };

  // Get initials from name
  const getInitials = (firstName, lastName) => {
    let initials = '';
    if (firstName) initials += firstName.charAt(0).toUpperCase();
    if (lastName) initials += lastName.charAt(0).toUpperCase();
    if (!initials) {
      // Use first letter of data type if no name available
      initials = dataType.charAt(0).toUpperCase();
    }
    return initials;
  };

  // Get background color based on name for profile icon
  const getProfileColor = name => {
    const colors = [
      '#1abc9c',
      '#2ecc71',
      '#3498db',
      '#9b59b6',
      '#f1c40f',
      '#e67e22',
      '#e74c3c',
      '#34495e',
      '#16a085',
      '#27ae60',
      '#2980b9',
      '#8e44ad',
      '#f39c12',
      '#d35400',
      '#c0392b',
      '#7f8c8d'
    ];

    // Hash the name to get a consistent color
    let hash = 0;
    if (name) {
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
    }

    return colors[Math.abs(hash) % colors.length];
  };

  // Format date for display
  const formatDate = dateString => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Handle edit record
  const handleEdit = id => {
    if (dataType === 'farmer') {
      navigate(`/farmer/edit/${id}`);
    } else if (dataType === 'farmland') {
      navigate(`/farm/edit/${id}`);
    } else if (dataType === 'cultivation') {
      navigate(`/cultivation/edit/${id}`);
    }
  };

  // Handle delete record
  const handleDelete = async id => {
    try {
      setDeletingId(id);

      if (dataType === 'farmer') {
        await offlineDBService.deleteFarmer(id);
      } else if (dataType === 'farmland') {
        // Placeholder for farm deletion
        await offlineDBService.deleteFarm?.(id);
      }

      // Refresh data
      await loadData(currentPage, searchTerm);
    } catch (error) {
      console.error(`Error deleting ${dataType}:`, error);
      setError(`Error deleting record: ${error.message}`);
    } finally {
      setDeletingId(null);
      setShowDeleteConfirm({});
    }
  };

  // Handle create new record
  const handleCreate = () => {
    if (dataType === 'farmer') {
      navigate('/farmer/create');
    } else if (dataType === 'farmland') {
      navigate('/farm/create');
    } else if (dataType === 'cultivation') {
      navigate('/cultivation/create');
    }
  };

  // Render pagination controls - always show at least one page
  const renderPaginationWithInfo = () => {
    const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
    const startItem =
      totalItems === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;
    const endItem = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

    const paginationItems = [];

    // Previous button
    paginationItems.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1 || totalItems === 0}
      />
    );

    // First page
    paginationItems.push(
      <Pagination.Item
        key={1}
        active={currentPage === 1}
        onClick={() => handlePageChange(1)}
        disabled={totalItems === 0}
      >
        1
      </Pagination.Item>
    );

    // Ellipsis if needed
    if (currentPage > 3) {
      paginationItems.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
    }

    // Pages around current
    for (
      let page = Math.max(2, currentPage - 1);
      page <= Math.min(totalPages - 1, currentPage + 1);
      page++
    ) {
      paginationItems.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
          disabled={totalItems === 0}
        >
          {page}
        </Pagination.Item>
      );
    }

    // Ellipsis if needed
    if (currentPage < totalPages - 2) {
      paginationItems.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
    }

    // Last page if not already included and more than one page
    if (totalPages > 1) {
      paginationItems.push(
        <Pagination.Item
          key={totalPages}
          active={currentPage === totalPages}
          onClick={() => handlePageChange(totalPages)}
          disabled={totalItems === 0}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    // Next button
    paginationItems.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages || totalItems === 0}
      />
    );

    return (
      <Row className="align-items-center mb-1">
        <Col xs={12} md={6} className="mb-3 mb-md-0">
          <div className="text-md-start text-center">
            Showing {startItem} to {endItem} of {totalItems}
          </div>
        </Col>
        <Col xs={12} md={6}>
          <Pagination className="justify-content-md-end justify-content-center mb-0">
            {paginationItems}
          </Pagination>
        </Col>
      </Row>
    );
  };

  // Render a single record card
  const renderCard = item => {
    const deleteConfirmKey = `delete-${item.id}`;

    if (dataType === 'farmer') {
      // Farmer card rendering
      const firstName = item.first_name || '';
      const lastName = item.last_name || '';
      const fullName = `${firstName} ${lastName}`.trim() || 'Unknown Farmer';
      const initials = getInitials(firstName, lastName);
      const profileColor = getProfileColor(fullName);
      const phoneNumber =
        item.mobile_number || item.phone_number || item.phone || 'No Phone';

      const syncStatus = item.synced ? 'Synced' : 'Pending';
      const syncVariant = item.synced ? 'success' : 'warning';

      // Get creation date if available
      const creationDate = item.createdAt ? formatDate(item.createdAt) : '';

      return (
        <Card
          key={item.id}
          style={{
            marginBottom: '12px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <Card.Header style={{ padding: '10px' }}>
            {/* First row: Farmer info */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px'
              }}
            >
              {/* Profile Icon */}
              <div
                style={{
                  backgroundColor: profileColor,
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  marginRight: '10px'
                }}
              >
                {initials}
              </div>

              {/* Farmer Info */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flexGrow: 1
                }}
              >
                <div
                  style={{
                    fontSize: '16px',
                    fontWeight: '500'
                  }}
                >
                  {fullName}
                </div>
                <div
                  style={{
                    fontSize: '14px',
                    color: '#6c757d'
                  }}
                >
                  {phoneNumber}
                </div>
                {creationDate && (
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#6c757d'
                    }}
                  >
                    Added: {creationDate}
                  </div>
                )}
              </div>

              <Badge
                bg={syncVariant}
                style={{
                  fontSize: '12px',
                  marginLeft: '8px'
                }}
              >
                {syncStatus}
              </Badge>
            </div>

            {/* Second row: Action buttons */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              {showDeleteConfirm[deleteConfirmKey] ? (
                // Confirmation buttons
                <>
                  <Button
                    variant="danger"
                    size="sm"
                    style={{
                      marginRight: '5px',
                      width: '32px',
                      height: '32px',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    disabled={deletingId === item.id}
                    onClick={() => handleDelete(item.id)}
                    title="Confirm Delete"
                  >
                    {deletingId === item.id ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <FaCheck />
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    style={{
                      width: '32px',
                      height: '32px',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={() => {
                      const newState = { ...showDeleteConfirm };
                      newState[deleteConfirmKey] = false;
                      setShowDeleteConfirm(newState);
                    }}
                    title="Cancel"
                  >
                    <FaTimes />
                  </Button>
                </>
              ) : (
                // Normal action buttons
                <>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    style={{
                      marginRight: '5px',
                      width: '32px',
                      height: '32px',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={() => handleEdit(item.id)}
                    title="Edit"
                  >
                    <MdEdit />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    style={{
                      width: '32px',
                      height: '32px',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={() => {
                      const newState = { ...showDeleteConfirm };
                      newState[deleteConfirmKey] = true;
                      setShowDeleteConfirm(newState);
                    }}
                    title="Delete"
                  >
                    <FaTrash />
                  </Button>
                </>
              )}
            </div>
          </Card.Header>
        </Card>
      );
    } else if (dataType === 'farmland') {
      // Farmland card rendering (simplified example)
      const farmName = item.farm_name || `Farm ${item.id}`;
      // const location = item.location || 'Unknown Location';
      const area = item.area
        ? `${item.area} ${item.area_unit || 'acres'}`
        : 'Area unknown';

      return (
        <Card
          key={item.id}
          style={{
            marginBottom: '12px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <Card.Header style={{ padding: '10px' }}>
            {/* First row: Farm info */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px'
              }}
            >
              {/* Farm Icon */}
              <div
                style={{
                  backgroundColor: '#27ae60',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  marginRight: '10px'
                }}
              >
                F
              </div>

              {/* Farm Info */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flexGrow: 1
                }}
              >
                <div
                  style={{
                    fontSize: '16px',
                    fontWeight: '500'
                  }}
                >
                  {farmName}
                </div>
                {/* <div
                  style={{
                    fontSize: '14px',
                    color: '#6c757d'
                  }}
                >
                  {location}
                </div> */}
                <div
                  style={{
                    fontSize: '14px',
                    color: '#6c757d'
                  }}
                >
                  {area}
                </div>
              </div>
            </div>

            {/* Second row: Action buttons */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              {showDeleteConfirm[deleteConfirmKey] ? (
                // Confirmation buttons
                <>
                  <Button
                    variant="danger"
                    size="sm"
                    style={{
                      marginRight: '5px',
                      width: '32px',
                      height: '32px',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    disabled={deletingId === item.id}
                    onClick={() => handleDelete(item.id)}
                    title="Confirm Delete"
                  >
                    {deletingId === item.id ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <FaCheck />
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    style={{
                      width: '32px',
                      height: '32px',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={() => {
                      const newState = { ...showDeleteConfirm };
                      newState[deleteConfirmKey] = false;
                      setShowDeleteConfirm(newState);
                    }}
                    title="Cancel"
                  >
                    <FaTimes />
                  </Button>
                </>
              ) : (
                // Normal action buttons
                <>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    style={{
                      marginRight: '5px',
                      width: '32px',
                      height: '32px',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={() => handleEdit(item.id)}
                    title="Edit"
                  >
                    <MdEdit />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    style={{
                      width: '32px',
                      height: '32px',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={() => {
                      const newState = { ...showDeleteConfirm };
                      newState[deleteConfirmKey] = true;
                      setShowDeleteConfirm(newState);
                    }}
                    title="Delete"
                  >
                    <FaTrash />
                  </Button>
                </>
              )}
            </div>
          </Card.Header>
        </Card>
      );
    } else if (dataType === 'cultivation') {
      // let farm = offlineDBService.getDropdownData;
      // console.log('asdfasdf');
      return (
        <Card
          key={item.id}
          style={{
            marginBottom: '12px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <Card.Header style={{ padding: '10px' }}>
            {/* First row: Cultivation info */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px'
              }}
            >
              {/* Cultivation Icon */}
              <div
                style={{
                  backgroundColor: '#3498db',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  marginRight: '10px'
                }}
              >
                C
              </div>

              {/* Cultivation Info */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flexGrow: 1
                }}
              >
                <div
                  style={{
                    fontSize: '16px',
                    fontWeight: '500'
                  }}
                >
                  {item.cropName || 'Unknown Crop'}
                </div>
                <div
                  style={{
                    fontSize: '14px',
                    color: '#6c757d'
                  }}
                >
                  Farm: {item.farmName || 'Unknown Farm'}
                </div>
                <div
                  style={{
                    fontSize: '14px',
                    color: '#6c757d'
                  }}
                >
                  Area: {item.cultivated_area} {'acres'}
                </div>
                <div
                  style={{
                    fontSize: '14px',
                    color: '#6c757d'
                  }}
                >
                  {item.sowing_date &&
                    `Sowing: ${formatDate(item.sowing_date)}`}
                  {item.sowing_date && item.expected_harvest_date && ' | '}
                  {item.expected_harvest_date &&
                    `Harvest: ${formatDate(item.expected_harvest_date)}`}
                </div>
              </div>

              <Badge
                bg={item.synced ? 'success' : 'warning'}
                style={{
                  fontSize: '12px',
                  marginLeft: '8px'
                }}
              >
                {item.synced ? 'Synced' : 'Pending'}
              </Badge>
            </div>

            {/* Second row: Action buttons */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              {showDeleteConfirm[deleteConfirmKey] ? (
                // Confirmation buttons
                <>
                  <Button
                    variant="danger"
                    size="sm"
                    style={{
                      marginRight: '5px',
                      width: '32px',
                      height: '32px',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    disabled={deletingId === item.id}
                    onClick={() => handleDelete(item.id)}
                    title="Confirm Delete"
                  >
                    {deletingId === item.id ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <FaCheck />
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    style={{
                      width: '32px',
                      height: '32px',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={() => {
                      const newState = { ...showDeleteConfirm };
                      newState[deleteConfirmKey] = false;
                      setShowDeleteConfirm(newState);
                    }}
                    title="Cancel"
                  >
                    <FaTimes />
                  </Button>
                </>
              ) : (
                // Normal action buttons
                <>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    style={{
                      marginRight: '5px',
                      width: '32px',
                      height: '32px',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={() => handleEdit(item.id)}
                    title="Edit"
                  >
                    <MdEdit />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    style={{
                      width: '32px',
                      height: '32px',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={() => {
                      const newState = { ...showDeleteConfirm };
                      newState[deleteConfirmKey] = true;
                      setShowDeleteConfirm(newState);
                    }}
                    title="Delete"
                  >
                    <FaTrash />
                  </Button>
                </>
              )}
            </div>
          </Card.Header>
        </Card>
      );
    }

    // Default fallback
    return (
      <Card key={item.id} style={{ marginBottom: '12px' }}>
        <Card.Body>
          <Card.Title>ID: {item.id}</Card.Title>
        </Card.Body>
      </Card>
    );
  };

  // Get title based on data type
  const getTitle = () => {
    switch (dataType) {
      case 'farmer':
        return 'Farmers Records';
      case 'farmland':
        return 'Farmlands Records';
      default:
        return 'Database Records';
    }
  };

  return (
    <>
      {/* Create button */}
      <Row style={{ marginBottom: '16px' }}>
        <Col
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center'
          }}
        >
          <Button
            variant="primary"
            onClick={handleCreate}
            style={{
              display: 'flex',
              alignItems: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <FaPlus style={{ marginRight: '8px' }} />
            Create New {dataType.charAt(0).toUpperCase() + dataType.slice(1)}
          </Button>
        </Col>
      </Row>
      <div className="d-flex flex-column h-100 card">
        <FalconCardHeader title={getTitle()} titleTag="h5" light={true} />

        {/* Search and Filters */}
        <Row style={{ marginBottom: '16px', padding: '10px' }}>
          <Col md={8} lg={4} style={{ marginBottom: '10px' }}>
            <Form onSubmit={handleSearch}>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder={`Search ${dataType}s...`}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  style={{ borderRadius: '4px 0 0 4px' }}
                />
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSearching}
                  style={{ borderRadius: '0 4px 4px 0' }}
                >
                  {isSearching ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FaSearch />
                  )}
                </Button>
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={resetSearch}
                    style={{ marginLeft: '5px' }}
                  >
                    Clear
                  </Button>
                )}
              </InputGroup>
            </Form>
          </Col>
          <Col
            md={4}
            lg={8}
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center'
            }}
          >
            <Button
              variant="outline-primary"
              onClick={() => loadData(currentPage, searchTerm)}
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <FaSyncAlt style={{ marginRight: '8px' }} /> Refresh
            </Button>
          </Col>
        </Row>

        {/* Data List */}
        {loading ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 0',
              color: '#6c757d'
            }}
          >
            <Spinner
              animation="border"
              role="status"
              style={{ marginBottom: '16px' }}
            >
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p style={{ fontSize: '16px' }}>Loading {dataType} data...</p>
          </div>
        ) : error ? (
          <Alert variant="danger" style={{ borderRadius: '6px' }}>
            {error}
          </Alert>
        ) : data.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 0',
              color: '#6c757d'
            }}
          >
            <FaUser size={40} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <h5 style={{ marginBottom: '8px' }}>No {dataType}s Found</h5>
            <p style={{ marginBottom: '16px' }}>
              {searchTerm
                ? `No ${dataType}s match your search criteria.`
                : `There are no ${dataType}s in the database.`}
            </p>
            {searchTerm && (
              <Button
                variant="outline-primary"
                onClick={resetSearch}
                style={{ borderRadius: '4px' }}
              >
                Clear Search
              </Button>
            )}
          </div>
        ) : (
          <Row style={{ padding: '10px' }}>
            <Col xs={12}>{data.map(item => renderCard(item))}</Col>
          </Row>
        )}

        {/* <CardFooter>{renderPagination()}</CardFooter> */}
        <CardFooter>{renderPaginationWithInfo()}</CardFooter>
      </div>
    </>
  );
}

DynamicOfflineReport.propTypes = {
  dataType: PropTypes.string
};

export default DynamicOfflineReport;
